import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthProvider.js';
import { reportError, Sentry } from '../sentry.js';
import { supabase } from '../supabase.js';
import { startBackfills } from './backfill.js';
import {
  beginDbStatsWindow,
  bindLocalDbUser,
  boundLocalDbUser,
  getLocalDb,
  preloadLocalDbEngine,
  readDbStats,
} from './db.js';
import { countPending } from './outbox.js';
import {
  pullAll,
  pullNutritionEssentials,
  pushOutbox,
  type RealtimeHandle,
  resetHouseholdWatermarks,
  subscribeRealtime,
} from './sync.js';
import { captureSyncDiagnostics } from './syncDiag.js';
import { logSync } from './syncLog.js';
import { ensureLeaderElection, getTabRole, subscribeTabRole, type TabRole } from './tabLeader.js';
import {
  beginMeterWindow,
  endMeterWindow,
  meterPhase,
  type MeterSnapshot,
  readMeter,
} from './transferMeter.js';

function stringifyError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof e.message === 'string' && e.message.length > 0) parts.push(e.message);
    if (typeof e.code === 'string') parts.push(`code=${e.code}`);
    if (typeof e.hint === 'string') parts.push(`hint=${e.hint}`);
    if (parts.length > 0) return parts.join(' · ');
    try {
      return JSON.stringify(err);
    } catch {
      return '[unserializable error]';
    }
  }
  return String(err);
}

function withTimeout<T>(
  p: Promise<T>,
  ms: number,
  label: string,
  onTimeout?: () => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      onTimeout?.();
      reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        // Preserve the original error so the outer catch can extract
        // shape (.message / .code / .hint on PostgrestErrors, etc).
        // Earlier this wrapped non-Errors in `new Error(String(e))`,
        // which collapses an object into "[object Object]" and hides
        // what actually went wrong.
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(e);
      },
    );
  });
}

export type SyncStatus = 'initializing' | 'idle' | 'syncing' | 'error' | 'offline';

interface SyncState {
  status: SyncStatus;
  /** SQLite is open; false only during first boot. */
  localReady: boolean;
  /** First sync cycle for the signed-in user has finished. */
  hydrated: boolean;
  /** @deprecated Prefer {@link localReady} or query `isLoading`. True once DB is open and the first pull finished (signed-in only). */
  isLocalReady: boolean;
  pendingWrites: number;
  lastSyncedAt: number | null;
  lastError: string | null;
  /** Wall-clock when the current 'syncing' cycle started, or null. */
  syncingSince: number | null;
  /**
   * Cross-tab role. Only the leader runs sync cycles + realtime; other
   * tabs read from local SQLite but skip writes/network to avoid
   * wedging the shared IndexedDB backing store.
   */
  tabRole: TabRole;
  /** Trigger an immediate pull + push cycle. Safe to call concurrently — overlapping calls coalesce. */
  syncNow: () => Promise<void>;
  /**
   * Flush the outbox directly, bypassing `cycle()`'s in-flight gate.
   * Use when a server-side action needs to see a locally-enqueued row
   * *now* and can't tolerate the race where syncNow returns an
   * in-flight cycle that already started its push before the new
   * outbox entry was added (e.g. ImportBakeoffNewPage seeding variants
   * for a freshly-uploaded batch).
   */
  flushOutbox: () => Promise<void>;
}

const SyncContext = createContext<SyncState | undefined>(undefined);

const INVALIDATE_DEBOUNCE_MS = 100;
const PULL_DEBOUNCE_MS = 2000;
// Hard cap on a single sync cycle. If something hangs (a long-tail
// fetch never resolving, supabase-js stalling around token refresh,
// etc.) we abort the await chain so the badge can leave "Syncing…"
// and the user can retry. The actual network requests don't get
// cancelled — we just stop waiting on them.
const CYCLE_TIMEOUT_MS = 180_000;
// How often the watchdog checks for a wedged status. The boot UX is
// "page reload always fixes it", which is exactly the symptom of a
// dangling 'syncing' setState that no one comes back to clear. The
// watchdog gives us a recovery path without a reload.
const WATCHDOG_INTERVAL_MS = 5_000;
// Co-members' libraries (other people's content) have no reliable
// per-row realtime signal — a recipe someone adds to an already-shared
// collection doesn't change any row we're subscribed to. So while we're
// in a household we poll on a slow cadence (and on tab focus) to pull
// their new content. Cheap: an incremental watermark pull returns
// nothing when nothing changed.
const HOUSEHOLD_POLL_MS = 30_000;

type SpanLike = { setAttributes?: (attrs: Record<string, number | string>) => void };

// Attach per-cycle measurements to the sync.cycle span so they're queryable in
// Sentry (durations + DB-lock contention + device context). Cheap + sync.
function annotateCycleSpan(
  span: SpanLike,
  m: { cycleStart: number; pushMs: number; pullMs: number; outcome: string; meter: MeterSnapshot },
): void {
  const stats = readDbStats();
  const deviceMemory = (navigator as unknown as { deviceMemory?: number }).deviceMemory ?? 0;
  span.setAttributes?.({
    'sync.cycle_ms': Date.now() - m.cycleStart,
    'sync.push_ms': m.pushMs,
    'sync.pull_ms': m.pullMs,
    'sync.outcome': m.outcome,
    'sync.tab_role': getTabRole(),
    'sync.db_ops': stats.ops,
    'sync.db_wait_ms': stats.totalWaitMs,
    'sync.db_max_op_ms': stats.maxRunMs,
    'sync.db_slowest': stats.slowestLabel,
    'sync.device_memory_gb': deviceMemory,
    'sync.bytes_pulled': m.meter.totalDown,
    'sync.bytes_pushed': m.meter.totalUp,
    'sync.requests': m.meter.totalRequests,
  });
}

/**
 * Persist a compact per-cycle data-transfer summary into
 * `sync_transfer_events` (one row per metered phase) for the /data-usage
 * page. Fire-and-forget + best-effort: a failed insert must never surface
 * as a sync error, and it runs after the meter window closes so it doesn't
 * measure itself.
 */
function recordTransfer(
  cycleId: string,
  meter: MeterSnapshot,
  pushMs: number,
  pullMs: number,
): void {
  const events = Object.entries(meter.phases)
    .filter(([, p]) => p.requests > 0)
    .map(([phase, p]) => {
      const direction = phase === 'push' ? 'push' : 'pull';
      return {
        direction,
        phase,
        rows: p.rows,
        bytes: direction === 'push' ? p.bytesUp : p.bytesDown,
        requests: p.requests,
        duration_ms: phase === 'push' ? pushMs : phase === 'pull' ? pullMs : 0,
      };
    });
  if (events.length === 0) return;
  // The new RPC isn't in the checked-in generated DB types yet (regenerated
  // out of band), so call it through a structural cast rather than the typed
  // overload — same approach as datausage/api.ts. Call it as a *member* of
  // `supabase` (not via a detached `const rpc = supabase.rpc`) so the method
  // keeps its `this` binding — supabase-js's rpc() reads `this.rest` and a
  // detached call throws "Cannot read properties of undefined (reading
  // 'rest')" synchronously, which — fired from cycle()'s finally — would
  // reject the whole sync cycle. This insert is best-effort and must never
  // surface as a sync error, so swallow both sync throws and async rejections.
  try {
    void supabase
      .rpc('record_sync_transfer' as never, { p_cycle_id: cycleId, p_events: events } as never)
      .then(
        ({ error }) => {
          if (error) logSync('warn', 'record_sync_transfer failed', { error: error.message });
        },
        (err: unknown) => {
          logSync('warn', 'record_sync_transfer threw', { error: stringifyError(err) });
        },
      );
  } catch (err) {
    logSync('warn', 'record_sync_transfer threw', { error: stringifyError(err) });
  }
}

export function SyncProvider({ children }: { children: React.ReactNode }) {
  const { user, householdId, loading: authLoading } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState<SyncStatus>('initializing');
  const [localReady, setLocalReady] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [pendingWrites, setPendingWrites] = useState(0);
  const [syncingSince, setSyncingSince] = useState<number | null>(null);
  const [tabRole, setTabRole] = useState<TabRole>(() => getTabRole());
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const currentAbort = useRef<AbortController | null>(null);
  const pullPending = useRef(false);
  const pullDebounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchInvalidateTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Wall-clock when status was most recently set to 'syncing'. Used by
  // the watchdog to detect a wedged state where the IIFE never reaches
  // its own finally (network hang, supabase-js auth queue stuck, etc).
  const syncingStartedAt = useRef<number | null>(null);

  // Keys deliberately excluded from the general per-cycle invalidate:
  //  - `collections`: full hydration is very expensive (every recipe in every
  //    collection); SQLite is already updated, so refetch picker/import keys only.
  //  - `search` / `search-embedded-count`: the semantic-search hooks. Their query
  //    functions load the ENTIRE recipe_embeddings table out of SQLite (a multi-
  //    second scan that unpacks every vector) — running that on the single
  //    serialized cr-sqlite connection every sync cycle serializes all the cheap
  //    sync reads behind it and wedges the whole engine (12–46s pulls, see the
  //    `db lock: still waiting` storm). They're driven by user input + a generous
  //    staleTime instead, and are refreshed explicitly via `scheduleSearchInvalidate`
  //    only when a recipe/embedding phase actually pulled rows.
  const SYNC_INVALIDATE_EXCLUDE = new Set(['collections', 'search', 'search-embedded-count']);

  function scheduleInvalidate() {
    clearTimeout(invalidateTimer.current);
    invalidateTimer.current = setTimeout(() => {
      void qc.invalidateQueries({
        predicate: (query) => !SYNC_INVALIDATE_EXCLUDE.has(query.queryKey[0] as string),
      });
    }, INVALIDATE_DEBOUNCE_MS);
  }

  function scheduleSearchInvalidate() {
    clearTimeout(searchInvalidateTimer.current);
    searchInvalidateTimer.current = setTimeout(() => {
      void qc.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'search' || query.queryKey[0] === 'search-embedded-count',
      });
    }, INVALIDATE_DEBOUNCE_MS);
  }

  async function refreshPendingCount() {
    try {
      setPendingWrites(await countPending());
    } catch {
      /* outbox not ready yet */
    }
  }

  function setSyncing() {
    const now = Date.now();
    syncingStartedAt.current = now;
    setSyncingSince(now);
    setStatus('syncing');
  }

  function setSettled(next: SyncStatus, error?: string) {
    syncingStartedAt.current = null;
    setSyncingSince(null);
    if (error !== undefined) setLastError(error);
    setStatus(next);
  }

  async function cycle(ownerId: string) {
    if (inFlight.current) {
      pullPending.current = true;
      logSync('info', 'cycle: coalesced into in-flight run');
      return inFlight.current;
    }
    const ac = new AbortController();
    currentAbort.current = ac;
    const run = Sentry.startSpan(
      { name: 'sync.cycle', op: 'sync', forceTransaction: true },
      async (span: SpanLike) => {
        const cycleStart = Date.now();
        const cycleId = crypto.randomUUID();
        // Reset the DB-op aggregator so the span reports contention during
        // exactly this cycle (incl. UI queries competing on the connection).
        beginDbStatsWindow();
        // Open the data-transfer meter so the instrumented fetch attributes
        // every request this cycle makes (see transferMeter.ts).
        beginMeterWindow();
        let pushMs = 0;
        let pullMs = 0;
        // Did this cycle actually change local state? Steady-state cycles
        // (realtime pokes, focus re-pulls) routinely pull 0 rows; invalidating
        // React Query then just re-runs every active query for nothing — and on
        // the single cr-sqlite connection that re-runs the multi-second
        // semantic-search embedding scan, wedging the engine. Only invalidate
        // when something landed.
        let anyChange = false;
        logSync('info', 'cycle: start');
        setSyncing();
        setLastError(null);
        try {
          // Push first so the pull sees our own changes reflected as
          // server-acknowledged state (avoids echo-induced overwrites).
          // Wrap in a hard timeout so a single hung request can't trap
          // the user on "Syncing…" forever — when the timeout fires we
          // also abort the AbortController so the inner drain loop
          // bails out instead of continuing to hit the network behind
          // our back (which would race a later cycle and trip
          // duplicate-key / FK violations on recipe pushes).
          logSync('info', 'cycle: invoking pushOutbox');
          meterPhase('push');
          const pushStart = Date.now();
          const pushRes = await Sentry.startSpan({ name: 'sync.push', op: 'sync.push' }, () =>
            withTimeout(pushOutbox(supabase, ownerId, ac.signal), CYCLE_TIMEOUT_MS, 'push', () =>
              ac.abort(),
            ),
          );
          pushMs = Date.now() - pushStart;
          if (pushRes.ok > 0) anyChange = true;
          logSync('info', 'cycle: pushOutbox returned', pushRes);
          logSync('info', 'cycle: invoking pullAll');
          const pullStart = Date.now();
          const pullRes = await Sentry.startSpan({ name: 'sync.pull', op: 'sync.pull' }, () =>
            withTimeout(
              pullAll(supabase, ownerId, ac.signal, {
                // Invalidate React Query incrementally so the library card
                // grid hydrates the moment recipes land, instead of waiting
                // for imports / conversion_rules / rewrite_jobs to finish.
                // Skip the 'collections' key from each invalidate (it's
                // expensive — see scheduleInvalidate predicate).
                onPhaseComplete: (phase, rows) => {
                  // Progressive first-load: unblock the UI gate as soon as
                  // recipe cards land (snapshot 'recipe_metadata' stage, or
                  // the legacy 'recipes' phase), rather than waiting for the
                  // whole cycle. Fires even on a 0-row phase (e.g. an
                  // already-hydrated or empty library) so the grid renders.
                  if (phase === 'recipe_metadata' || phase === 'recipes') {
                    setHydrated(true);
                  }
                  // No-op phase → nothing landed → no invalidation needed.
                  if (rows === 0) return;
                  anyChange = true;
                  scheduleInvalidate();
                  // The search/embedding queries are excluded from the general
                  // invalidate (too expensive to re-run per cycle); refresh them
                  // only when recipe or embedding data actually changed.
                  if (
                    phase === 'recipe_metadata' ||
                    phase === 'recipes' ||
                    phase === 'recipe_embeddings'
                  ) {
                    scheduleSearchInvalidate();
                  }
                },
              }),
              CYCLE_TIMEOUT_MS,
              'pull',
              () => ac.abort(),
            ),
          );
          pullMs = Date.now() - pullStart;
          // Household-shared content is pulled inside pullAll but doesn't route
          // through onPhaseComplete, so fold its change signal in here. Covers
          // co-members' recipe/embedding edits too (not just collections), so
          // the search index refreshes when shared content changes.
          if (pullRes.householdChanges > 0) {
            anyChange = true;
            scheduleSearchInvalidate();
          }
          logSync('info', 'cycle: pullAll returned');
          // Fire-and-forget: reference data doesn't gate the UI, and
          // failures shouldn't show up as a sync error to the user.
          // Throttled internally — only refetches once a month.
          pullNutritionEssentials(supabase).catch((err: unknown) => {
            logSync('warn', 'nutrition essentials pull failed', { error: stringifyError(err) });
          });
          setLastSyncedAt(Date.now());
          setSettled('idle');
          annotateCycleSpan(span, {
            cycleStart,
            pushMs,
            pullMs,
            outcome: 'ok',
            meter: readMeter(),
          });
          logSync('info', `cycle: idle (took ${Date.now() - cycleStart}ms)`);
        } catch (err) {
          // Some thrown values (notably PostgrestError-shaped objects)
          // aren't Error instances, so `.message` is undefined and the
          // badge ends up showing "[object Object]". Stringify whatever
          // we got and prefer fields commonly populated by errors.
          const msg = stringifyError(err);
          // Dump the raw shape to the console so a debug session can see
          // what actually came back — useful when the stringified version
          // collapses an interesting object into something like
          // "PostgrestError" with no hint of the underlying cause.
          console.error('[sync] cycle threw:', err);
          setSettled('error', msg);
          logSync('error', `cycle: error after ${Date.now() - cycleStart}ms`, { error: msg });
          // The cycle catch only sees pull / cycle-timeout failures (pushOutbox
          // handles its own per-entry errors internally). Report them so prod
          // sync stalls — e.g. a pull statement timing out — are visible.
          reportError(err, { operation: 'sync_cycle' });
          const outcome = /timed out/.test(msg) ? 'timeout' : 'error';
          annotateCycleSpan(span, { cycleStart, pushMs, pullMs, outcome, meter: readMeter() });
          // Auto-capture a trimmed diagnostic (throttled) so a wedged device
          // surfaces without the user manually uploading logs.
          void captureSyncDiagnostics({
            status: 'error',
            lastError: msg,
            syncingForMs: Date.now() - cycleStart,
            outcome,
          });
        } finally {
          // Close the meter BEFORE recording so the metrics insert isn't
          // counted as cycle traffic, then persist a compact per-cycle
          // summary for /data-usage (best-effort, fire-and-forget).
          endMeterWindow();
          recordTransfer(cycleId, readMeter(), pushMs, pullMs);
          if (currentAbort.current === ac) currentAbort.current = null;
          await refreshPendingCount();
          // Only refresh React Query when the cycle actually changed local
          // state (push applied rows, a pull phase landed rows, or household
          // content arrived). A 0-row steady-state cycle invalidates nothing,
          // so active queries — including the expensive search scan — don't
          // re-run on the serialized connection for no reason.
          if (anyChange) scheduleInvalidate();
        }
      },
    );
    inFlight.current = run;
    try {
      await run;
    } finally {
      inFlight.current = null;
      if (pullPending.current) {
        pullPending.current = false;
        void cycle(ownerId);
      }
    }
  }

  function schedulePull(ownerId: string) {
    clearTimeout(pullDebounceTimer.current);
    pullDebounceTimer.current = setTimeout(() => {
      void cycle(ownerId);
    }, PULL_DEBOUNCE_MS);
  }

  // Boot: bind the local DB to the signed-in account, then open it before any
  // page tries to read. One account per page load (see db.ts): when a
  // *different* account signs in (here, or in another tab), reload so the
  // previous account's handle, any in-flight sync still holding its owner id,
  // and every in-memory cache go with it. Signing out alone doesn't reload —
  // no signed-out surface reads the DB, and signing back in as the same
  // account reuses the open handle. Signed-out visitors never open it at all.
  // Warm the (account-independent) SQLite engine while auth resolves, so
  // signing in only has to open the account's file.
  useEffect(() => {
    void preloadLocalDbEngine().catch(() => {
      // Surfaces (and auto-recovers) through getLocalDb() on the real open.
    });
  }, []);

  useEffect(() => {
    // Signed out: nothing to open (readiness is derived below).
    if (authLoading || !user) return;
    if (bindLocalDbUser(user.id) === 'reload') {
      logSync('info', 'account changed — reloading to open its own database');
      location.reload();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await getLocalDb();
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setLastError(stringifyError(err));
        }
        return;
      }
      await refreshPendingCount();
      if (!cancelled) {
        setLocalReady(true);
        // Only flip to 'idle' if no cycle has already moved us to
        // 'syncing'. Without this, db init resolving AFTER the cycle
        // started (cycle awaits the same getLocalDb promise) lets the
        // boot effect clobber status back to 'idle' mid-pull — the
        // badge says "Synced" while pull is still draining and other
        // pages contend on the SQLite mutex.
        setStatus((s) => (s === 'initializing' ? 'idle' : s));
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id]);

  // Kick off cross-tab leader election. Subscribe so role changes
  // (e.g. another tab closing makes us the leader) are reflected.
  useEffect(() => {
    ensureLeaderElection();
    setTabRole(getTabRole());
    const unsub = subscribeTabRole(setTabRole);
    return unsub;
  }, []);

  // When the user changes (or we become the leader), kick off an
  // initial sync and open a realtime channel. Follower tabs skip both
  // — they read from local SQLite but the leader is the sole writer
  // and the sole realtime listener (avoids two tabs racing on the
  // shared IDB and on Supabase row events).
  useEffect(() => {
    if (!user) {
      setHydrated(false);
      return;
    }
    if (tabRole !== 'leader') {
      logSync('info', `skipping sync cycle: tab role is ${tabRole}`);
      // Followers still count as hydrated for the UI gate — the
      // existing local cache from a prior session is good enough.
      setHydrated(true);
      return;
    }
    const ownerId = user.id;
    let handle: RealtimeHandle | undefined;
    let cancelled = false;
    setHydrated(false);
    (async () => {
      await cycle(ownerId);
      if (cancelled) return;
      setHydrated(true);
      // First sync settled — run any pending one-time local backfills off the
      // critical path, pausing whenever a cycle is in flight so a chunk never
      // competes with an active pull on the single connection.
      void startBackfills({ shouldPause: () => inFlight.current !== null });
      handle = subscribeRealtime(
        supabase,
        ownerId,
        {
          onLocalUpdate: scheduleInvalidate,
          onNeedsPull: () => schedulePull(ownerId),
          // A co-member's membership / sharing change: the freshly-shared
          // back-catalog predates our watermark, so reset it and re-pull.
          onHouseholdChanged: () => {
            void resetHouseholdWatermarks(ownerId).then(() => schedulePull(ownerId));
          },
        },
        // From the JWT claim, so the channel re-subscribes (effect dep) with
        // the right household bindings after a join/leave.
        householdId,
      );
    })();
    return () => {
      cancelled = true;
      void handle?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tabRole, householdId]);

  // Poll for co-members' new content while in a household: re-pull on
  // tab focus and on a slow interval. This is what makes a recipe a
  // co-member adds *after* sharing show up without a manual refresh —
  // there's no per-row realtime event we can subscribe to for it.
  useEffect(() => {
    if (!user || tabRole !== 'leader' || !householdId) return;
    const ownerId = user.id;
    function repullHousehold() {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void cycle(ownerId);
    }
    window.addEventListener('focus', repullHousehold);
    document.addEventListener('visibilitychange', repullHousehold);
    const poll = setInterval(repullHousehold, HOUSEHOLD_POLL_MS);
    return () => {
      window.removeEventListener('focus', repullHousehold);
      document.removeEventListener('visibilitychange', repullHousehold);
      clearInterval(poll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tabRole, householdId]);

  useEffect(
    () => () => {
      clearTimeout(invalidateTimer.current);
      clearTimeout(searchInvalidateTimer.current);
      clearTimeout(pullDebounceTimer.current);
    },
    [],
  );

  // Watchdog: if status has been 'syncing' for longer than the cycle
  // timeout, force-recover. The withTimeout inside cycle already
  // guards the awaited path, but this catches state-machine bugs
  // where setStatus('syncing') was issued and the matching settle
  // never landed (e.g., an aborted effect that left the badge stale).
  useEffect(() => {
    if (status !== 'syncing') return;
    const tick = setInterval(() => {
      const startedAt = syncingStartedAt.current;
      if (!startedAt) return;
      if (Date.now() - startedAt <= CYCLE_TIMEOUT_MS) return;
      // Clear inFlight AND abort the cycle's signal so the inner
      // drain loop bails. Without the abort, the in-flight pushOutbox
      // / pullAll keep running in the background and race against the
      // next cycle (concurrent recipe pushes manifested as duplicate
      // PK / FK violations on instruction_ingredient_refs).
      currentAbort.current?.abort();
      inFlight.current = null;
      logSync('error', 'watchdog: cycle stalled, aborting + clearing inFlight');
      setSettled('error', 'Sync stalled — click "Syncing…" or refresh to retry.');
    }, WATCHDOG_INTERVAL_MS);
    return () => clearInterval(tick);
  }, [status]);

  // Online/offline transitions trigger a catch-up sync.
  useEffect(() => {
    if (!user) return;
    function onOnline() {
      void cycle(user!.id);
    }
    function onOffline() {
      setStatus('offline');
    }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // A signed-out visitor never opens the DB (it's bound per account), so
  // there's nothing to wait for — ready and idle once auth has resolved.
  const signedOutReady = !authLoading && !user && !boundLocalDbUser();
  const effectiveLocalReady = localReady || signedOutReady;
  const isLocalReady = effectiveLocalReady && (!user || hydrated);

  const value: SyncState = {
    status: signedOutReady && status === 'initializing' ? 'idle' : status,
    localReady: effectiveLocalReady,
    hydrated,
    isLocalReady,
    pendingWrites,
    lastSyncedAt,
    lastError,
    syncingSince,
    tabRole,
    syncNow: async () => {
      if (!user) return;
      if (tabRole !== 'leader') {
        logSync('warn', 'syncNow ignored: this tab is not the sync leader');
        return;
      }
      await cycle(user.id);
    },
    flushOutbox: async () => {
      if (!user) return;
      if (tabRole !== 'leader') {
        logSync('warn', 'flushOutbox ignored: this tab is not the sync leader');
        return;
      }
      try {
        await pushOutbox(supabase, user.id);
      } finally {
        await refreshPendingCount();
      }
    },
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}

export function useSync(): SyncState {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
}

/** Gate for React Query hooks that read the local SQLite cache (no initial-sync wait). */
export function useLocalQueryEnabled(): boolean {
  return useLocalDbReady();
}

/** Gate for lightweight reads once SQLite is open (no initial sync wait). */
export function useLocalDbReady(): boolean {
  const { user } = useAuth();
  const { localReady } = useSync();
  return !!user && localReady;
}
